import React, { useState, useEffect, useContext } from 'react';
import { Copy, Check, User, Link, Calendar, CreditCard, Server, Bell, ChevronDown, ChevronRight, Info, Zap } from 'lucide-react';
import TagInsertContext from '../../contexts/TagInsertContext';
import './TagsPanel.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5015/api/v1';

// Icon mapping for categories
const CATEGORY_ICONS = {
  user: User,
  action: Link,
  booking: Calendar,
  transaction: CreditCard,
  platform: Server,
  notification: Bell,
};

// Fallback registry for platform emails
const FALLBACK_REGISTRY = {
  categories: [
    {
      id: 'user',
      label: 'User',
      icon: 'user',
      variables: [
        { key: 'user.name', label: 'User Name', example: 'John Doe' },
        { key: 'user.firstName', label: 'First Name', example: 'John' },
        { key: 'user.email', label: 'Email', example: 'john@example.com' },
      ],
    },
    {
      id: 'action',
      label: 'Action Links',
      icon: 'link',
      variables: [
        { key: 'resetLink', label: 'Reset Password Link', example: 'https://knkt.io/reset/abc123' },
        { key: 'verifyLink', label: 'Verify Email Link', example: 'https://knkt.io/verify/xyz789' },
        { key: 'loginLink', label: 'Login Link', example: 'https://knkt.io/login' },
        { key: 'unsubscribeUrl', label: 'Unsubscribe Link', example: 'https://knkt.io/unsubscribe' },
      ],
    },
    {
      id: 'booking',
      label: 'Booking',
      icon: 'calendar',
      variables: [
        { key: 'booking.id', label: 'Booking ID', example: 'BK-123456' },
        { key: 'booking.date', label: 'Booking Date', example: 'January 15, 2026' },
        { key: 'booking.time', label: 'Booking Time', example: '2:00 PM' },
        { key: 'booking.service', label: 'Service Name', example: 'Music Consultation' },
      ],
    },
    {
      id: 'platform',
      label: 'Platform',
      icon: 'server',
      variables: [
        { key: 'platform.name', label: 'Platform Name', example: 'KNKT' },
        { key: 'platform.supportEmail', label: 'Support Email', example: 'support@knkt.io' },
        { key: 'platform.year', label: 'Current Year', example: '2026' },
      ],
    },
  ],
};

const TagsPanel = ({ onInsertVariable }) => {
  const [registry, setRegistry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(['user', 'action']);
  const [copiedKey, setCopiedKey] = useState(null);
  const [insertedKey, setInsertedKey] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Get the tag insert context (may be null if not wrapped in provider)
  const tagInsertContext = useContext(TagInsertContext);

  useEffect(() => {
    loadRegistry();
  }, []);

  const loadRegistry = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${API_BASE}/variables/registry`);
      const data = await response.json();
      if (data.success) {
        setRegistry(data.data);
      } else {
        throw new Error(data.message || 'Failed to load registry');
      }
    } catch (err) {
      console.error('Failed to load variable registry:', err);
      setError(err.message);
      setRegistry(FALLBACK_REGISTRY);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleTagClick = async (variable) => {
    const text = variable.isBlock ? variable.example : `{{${variable.key}}}`;

    // Try to insert using the context first
    if (tagInsertContext && tagInsertContext.hasActiveField) {
      const result = tagInsertContext.insertTag(text);
      if (result.inserted) {
        setInsertedKey(variable.key);
        setTimeout(() => setInsertedKey(null), 1500);

        // Also call the callback if provided
        if (onInsertVariable) {
          onInsertVariable(text);
        }
        return;
      }
    }

    // Fallback to clipboard
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(variable.key);
      setTimeout(() => setCopiedKey(null), 2000);

      // Also call the insert handler if provided
      if (onInsertVariable) {
        onInsertVariable(text);
      }
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getFilteredCategories = () => {
    if (!registry?.categories) return [];
    if (!searchQuery.trim()) return registry.categories;

    const query = searchQuery.toLowerCase();

    return registry.categories
      .map((category) => ({
        ...category,
        variables: category.variables.filter(
          (v) =>
            v.label.toLowerCase().includes(query) ||
            v.key.toLowerCase().includes(query) ||
            (v.description && v.description.toLowerCase().includes(query))
        ),
      }))
      .filter((category) => category.variables.length > 0);
  };

  const filteredCategories = getFilteredCategories();
  const hasActiveField = tagInsertContext?.hasActiveField;

  if (loading) {
    return (
      <div className="tags-panel">
        <div className="tags-panel-loading">Loading tags...</div>
      </div>
    );
  }

  return (
    <div className="tags-panel">
      <div className="tags-panel-header">
        <h4 className="tags-panel-title">PLATFORM TAGS</h4>
        <div className="tags-panel-info">
          <Info size={14} />
          <span>
            {hasActiveField
              ? 'Click to insert into active field'
              : 'Click a text field, then click a tag'}
          </span>
        </div>
      </div>

      {hasActiveField && (
        <div className="tags-panel-active-indicator">
          <Zap size={12} />
          <span>Ready to insert - click any tag</span>
        </div>
      )}

      <div className="tags-panel-description">
        Tags let you add personalized information to system emails.
        <div className="tags-panel-example">
          <code>{'{{user.name | "User"}}'}</code>
          <span className="example-arrow"></span>
          <span>with default value</span>
        </div>
      </div>

      <div className="tags-panel-search">
        <input
          type="text"
          placeholder="Search tags..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="tags-search-input"
        />
      </div>

      {error && (
        <div className="tags-panel-error">
          <span>Using offline tags. {error}</span>
          <button onClick={loadRegistry} className="retry-btn">Retry</button>
        </div>
      )}

      <div className="tags-panel-categories">
        {filteredCategories.map((category) => {
          const IconComponent = CATEGORY_ICONS[category.id] || Server;
          const isExpanded = expandedCategories.includes(category.id) || searchQuery.trim();

          return (
            <div key={category.id} className="tags-category">
              <button
                className="tags-category-header"
                onClick={() => toggleCategory(category.id)}
              >
                <div className="category-header-left">
                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  <IconComponent size={14} className="category-icon" />
                  <span className="category-label">{category.label}</span>
                </div>
                <span className="category-count">{category.variables.length}</span>
              </button>

              {isExpanded && (
                <div className="tags-category-content">
                  {category.variables.map((variable) => (
                    <button
                      key={variable.key}
                      className={`tag-item ${hasActiveField ? 'can-insert' : ''}`}
                      onClick={() => handleTagClick(variable)}
                      title={hasActiveField
                        ? `Click to insert {{${variable.key}}}`
                        : `Click to copy {{${variable.key}}}`}
                    >
                      <div className="tag-item-main">
                        <code className="tag-code">
                          {variable.isBlock ? variable.example : `{{${variable.key}}}`}
                        </code>
                        <span className="tag-copy-icon">
                          {insertedKey === variable.key ? (
                            <Zap size={14} className="inserted" />
                          ) : copiedKey === variable.key ? (
                            <Check size={14} className="copied" />
                          ) : (
                            <Copy size={14} />
                          )}
                        </span>
                      </div>
                      {variable.description && (
                        <span className="tag-description">{variable.description}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {filteredCategories.length === 0 && searchQuery && (
          <div className="tags-no-results">
            No tags found matching "{searchQuery}"
          </div>
        )}
      </div>

      <div className="tags-panel-footer">
        <div className="tags-syntax-help">
          <strong>Syntax Help:</strong>
          <ul>
            <li><code>{'{{variable}}'}</code> - Insert value</li>
            <li><code>{'{{variable | "default"}}'}</code> - With fallback</li>
            <li><code>{'{{#if variable}}...{{/if}}'}</code> - Conditional</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default TagsPanel;
